import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Container,
  Row,
  Col,
  Form,
  Button,
  Offcanvas,
  Accordion,
  Badge,
  Spinner,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { getUserPracticeById } from "../../api";
import { getAllEras } from "../../afinanceapi";
import { useNavigate } from "react-router-dom";
import "./CreateInvoice.css";

export default function ERAList() {
  const navigate = useNavigate();
  const [eras, setERAs] = useState([]);
  const [practiceList, setPracticeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({
    key: "header.ERADate",
    direction: "desc",
  });

  // Filters
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [selectedMedicalAids, setSelectedMedicalAids] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Temporary filters for Offcanvas
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpMedicalAids, setTmpMedicalAids] = useState([]);
  const [tmpStartDate, setTmpStartDate] = useState("");
  const [tmpEndDate, setTmpEndDate] = useState("");

  // UI
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  const currency = new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  });

  // Initialize date range (last 7 days)
  useEffect(() => {
    const today = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const iso = (d) => d.toISOString().slice(0, 10);
    setStartDate(iso(start));
    setEndDate(iso(today));
    setTmpStartDate(iso(start));
    setTmpEndDate(iso(today));
  }, []);

  // Fetch user practices and ERAs
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const token = sessionStorage.getItem("user");
        if (!token) throw new Error("No user token found");
        const user = jwtDecode(token);
        const practices = await getUserPracticeById(user._id);
        setPracticeList(practices || []);

        const practiceNumbers = (practices || []).map((p) => p.practiceNumber);
        const erasData = await getAllEras(practiceNumbers);
        setERAs(erasData || []);
      } catch (err) {
        console.error("Error fetching ERAs:", err);
        setERAs([]);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleRowClick = (era) => {
    navigate(`/billing/era/${era._id}`);
  };

  /* ---------- Dynamic filter options ---------- */

  // Only include practices that have ERA records
  const practicesInData = useMemo(() => {
    const found = new Set(
      eras.map((e) => e.header?.PracticeNumber).filter(Boolean)
    );
    return practiceList.filter((p) => found.has(p.practiceNumber));
  }, [practiceList, eras]);

  const medicalAidOptions = useMemo(() => {
    const set = new Set(
      eras.map((e) => e.header?.MedicalAidName).filter(Boolean)
    );
    return Array.from(set).sort();
  }, [eras]);

  const toggleIn = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  /* ---------- Filtering ---------- */
  const filteredERAs = useMemo(() => {
    return eras.filter((era) => {
      const h = era.header || {};
      const eraDate = h.ERADate ? new Date(h.ERADate) : null;
      const fromOK = !startDate || (eraDate && eraDate >= new Date(startDate));
      const toOK = !endDate || (eraDate && eraDate <= new Date(endDate));
      const practiceOK =
        selectedPractices.length === 0 ||
        selectedPractices.includes(h.PracticeNumber);
      const medicalAidOK =
        selectedMedicalAids.length === 0 ||
        selectedMedicalAids.includes(h.MedicalAidName);
      return fromOK && toOK && practiceOK && medicalAidOK;
    });
  }, [eras, selectedPractices, selectedMedicalAids, startDate, endDate]);

  /* ---------- Sorting ---------- */
  const sortedERAs = useMemo(() => {
    const arr = [...filteredERAs];
    const get = (obj, path) =>
      path.split(".").reduce((o, key) => (o ? o[key] : ""), obj);
    arr.sort((a, b) => {
      const valA = get(a, sortConfig.key);
      const valB = get(b, sortConfig.key);
      if (!isNaN(valA) && !isNaN(valB))
        return sortConfig.direction === "asc" ? valA - valB : valB - valA;
      return sortConfig.direction === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
    return arr;
  }, [filteredERAs, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedERAs.length / limit));
  const pagedERAs = sortedERAs.slice((page - 1) * limit, page * limit);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="fa-solid fa-arrows-up-down text-primary ms-2" />;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  /* ---------- Filter logic ---------- */
  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpMedicalAids(selectedMedicalAids);
    setTmpStartDate(startDate);
    setTmpEndDate(endDate);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedMedicalAids(tmpMedicalAids);
    setStartDate(tmpStartDate);
    setEndDate(tmpEndDate);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setSelectedPractices([]);
    setSelectedMedicalAids([]);
    const today = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    const iso = (d) => d.toISOString().slice(0, 10);
    setStartDate(iso(start));
    setEndDate(iso(today));
    setTmpPractices([]);
    setTmpMedicalAids([]);
    setTmpStartDate(iso(start));
    setTmpEndDate(iso(today));
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedMedicalAids.length ? 1 : 0) +
    (startDate && endDate ? 1 : 0);

  /* ---------- Render ---------- */
  return (
    <Container fluid className="pt-2">
      {/* Top bar: filters + pagination aligned right */}
      <Row className="mb-3 align-items-center justify-content-end">
        <Col md="auto" className="d-flex gap-2 flex-wrap justify-content-end">
          <Button
            variant="primary"
            onClick={openFilters}
            style={{ width: "120px" }}
          >
            <i className="fa-solid fa-filter me-2" />
            Filter{" "}
            {activeCount > 0 && (
              <Badge className="ms-2" bg="light" text="dark">
                {activeCount}
              </Badge>
            )}
          </Button>
          <Button
            variant="outline-danger"
            onClick={clearFilters}
            style={{ width: "120px" }}
            disabled={
              selectedPractices.length === 0 &&
              selectedMedicalAids.length === 0 &&
              !startDate &&
              !endDate
            }
          >
            <i className="fa-solid fa-trash me-2" />
            Filters
          </Button>

          {/* Pagination */}
          <div className="d-flex align-items-center gap-2 ms-2">
            <Button
              variant="primary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fa-solid fa-angle-left" />
            </Button>
            <div className="text-nowrap small">
              Page {page} of {totalPages}
            </div>
            <Button
              variant="primary"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fa-solid fa-angle-right" />
            </Button>
          </div>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p>Loading ERA logs...</p>
        </div>
      ) : pagedERAs.length === 0 ? (
        <p>No ERAs found. The default filter is for the last 7 days.</p>
      ) : (
        <Table
          bordered
          hover
          responsive
          className="fixed-table text-center align-middle"
        >
          <thead className="table-primary">
            <tr>
              <th style={{ cursor: "pointer", width: "2%" }}>#</th>
              <th
                onClick={() => handleSort("header.ERADate")}
                style={{ cursor: "pointer", width: "8%" }}
                className="text-primary fw-semibold"
              >
                ERA Date {renderSortIcon("header.ERADate")}
              </th>
              <th
                onClick={() => handleSort("header.MedKreditERAnbr")}
                style={{ cursor: "pointer", width: "10%" }}
                className="text-primary fw-semibold"
              >
                Switch Ref {renderSortIcon("header.MedKreditERAnbr")}
              </th>
              <th
                onClick={() => handleSort("header.PracticeNumber")}
                style={{ cursor: "pointer", width: "7%" }}
                className="text-primary fw-semibold"
              >
                Practice {renderSortIcon("header.PracticeNumber")}
              </th>
              <th
                onClick={() => handleSort("header.MedicalAidName")}
                style={{ cursor: "pointer", width: "18%" }}
                className="text-primary fw-semibold"
              >
                Medical Aid {renderSortIcon("header.MedicalAidName")}
              </th>
              <th
                onClick={() => handleSort("header.TotalPaidProvider")}
                style={{ cursor: "pointer", width: "10%" }}
                className="text-primary fw-semibold"
              >
                Paid to Prov {renderSortIcon("header.TotalPaidProvider")}
              </th>
              <th
                onClick={() => handleSort("header.TotalPaidMember")}
                style={{ cursor: "pointer", width: "10%" }}
                className="text-primary fw-semibold"
              >
                Paid to Mem {renderSortIcon("header.TotalPaidMember")}
              </th>
              <th
                onClick={() => handleSort("header.TotalNotPaid")}
                style={{ cursor: "pointer", width: "10%" }}
                className="text-primary fw-semibold"
              >
                Not Paid {renderSortIcon("header.TotalNotPaid")}
              </th>
              <th
                onClick={() => handleSort("header.PaymentPaidDate")}
                style={{ cursor: "pointer", width: "10%" }}
                className="text-primary fw-semibold"
              >
                Payment Date {renderSortIcon("header.PaymentPaidDate")}
              </th>
              <th
                onClick={() => handleSort("header.PaymentReference")}
                style={{ cursor: "pointer", width: "10%" }}
                className="text-primary fw-semibold"
              >
                Payment Ref {renderSortIcon("header.PaymentReference")}
              </th>
            </tr>
          </thead>
          <tbody>
            {pagedERAs.map((era, idx) => {
              const h = era.header || {};
              return (
                <tr
                  key={era._id}
                  onClick={() => handleRowClick(era)}
                  style={{ cursor: "pointer" }}
                >
                  <td>{(page - 1) * limit + idx + 1}</td>
                  <td>{h.ERADate}</td>
                  <td>{h.MedKreditERAnbr}</td>
                  <td>{h.PracticeNumber}</td>
                  <td>{h.MedicalAidName}</td>
                  <td className="text-end">
                    {currency.format(h.TotalPaidProvider || 0)}
                  </td>
                  <td className="text-end">
                    {currency.format(h.TotalPaidMember || 0)}
                  </td>
                  <td className="text-end">
                    {currency.format(h.TotalNotPaid || 0)}
                  </td>
                  <td>{h.PaymentPaidDate}</td>
                  <td>{h.PaymentReference}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      {/* Filter Offcanvas */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {/* Date Range */}
          <div className="mb-3">
            <h6 className="mb-1 text-primary fw-normal ms-3 mb-2">
              Date range
            </h6>
            <div className="d-flex gap-2 ms-1">
              <Form.Control
                type="date"
                value={tmpStartDate}
                onChange={(e) => setTmpStartDate(e.target.value)}
              />
              <Form.Control
                type="date"
                value={tmpEndDate}
                onChange={(e) => setTmpEndDate(e.target.value)}
              />
            </div>
          </div>

          <Accordion alwaysOpen>
            {/* Practices */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() =>
                      setTmpPractices(
                        practicesInData.map((p) => p.practiceNumber)
                      )
                    }
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices([])}
                  >
                    Clear
                  </Button>
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ maxHeight: 240, overflowY: "auto" }}
                >
                  {practicesInData.map((practice) => (
                    <Form.Check
                      key={practice._id}
                      type="checkbox"
                      id={`practice-${practice.practiceNumber}`}
                      label={`${practice.practiceNumber} — ${practice.practiceName}`}
                      checked={tmpPractices.includes(practice.practiceNumber)}
                      onChange={() =>
                        setTmpPractices((arr) =>
                          toggleIn(arr, practice.practiceNumber)
                        )
                      }
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Medical Aids */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>Medical Aid</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpMedicalAids(medicalAidOptions)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpMedicalAids([])}
                  >
                    Clear
                  </Button>
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ maxHeight: 240, overflowY: "auto" }}
                >
                  {medicalAidOptions.map((name) => (
                    <Form.Check
                      key={name}
                      type="checkbox"
                      id={`medaid-${name}`}
                      label={name}
                      checked={tmpMedicalAids.includes(name)}
                      onChange={() =>
                        setTmpMedicalAids((arr) => toggleIn(arr, name))
                      }
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Apply / Clear buttons */}
          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              style={{ width: "120px" }}
              variant="outline-danger"
              onClick={() => {
                setTmpPractices([]);
                setTmpMedicalAids([]);
                setTmpStartDate("");
                setTmpEndDate("");
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Remove
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
